import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import {Box, Chip, DialogContent, Stack, Tab, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {pink} from '@mui/material/colors';
import {object} from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import AreaIndicator from '../common/AreaIndicator.jsx';
import AvailableTabContent from './AvailableTabContent.jsx';
import CompletedTabContent from './CompletedTabContent.jsx';
import ProductsDropDown from './ProductsDropDown.jsx';
import Map from './QuotaManagerMap.jsx';
import {useGetFeatures} from '@app/api/features';
import {useGetProducts} from '@app/api/products';
import {useGetReservationsByProduct} from '@app/api/quotaReservations';
import {
  getDefaultFeatureStyle,
  getSelectedStyle,
} from '@app/styles/mapStyles.js';
import {getHashFromId} from '@app/util/granero';
import ErrorMessage from '@components/common/ErrorMessage';

const DEFAULT_POLL_MS = 5 * 60 * 1000; // 5 minutes in ms
const SHORT_POLL_MS = 30 * 1000; //30 milliseconds

const QuotaManagerContent = ({collection}) => {
  const [activeTab, setActiveTab] = useState('available');
  const [selectedProduct, setSelectedProduct] = useState();
  const [toReserveSelectedFeatureRefs, setToReserveSelectedFeatureRefs] =
    useState([]);
  const [reservationsVisibleFeatures, setReservationsVisibleFeatures] =
    useState({});

  const [zoomedFeature, setZoomedFeature] = useState();
  const [featuresToReserve, setFeaturesToReserve] = useState({});

  const [reservationPollIntervalMs, setReservationPollIntervalMs] =
    useState(DEFAULT_POLL_MS);

  const {
    data: products,
    isLoading: loadingProducts,
    error: productsError,
    refetch: refetchProducts,
  } = useGetProducts();

  const {data: featuresData, isLoading: loadingFeatures} = useGetFeatures(
    {
      collectionId: collection?.id,
      maxItems: Infinity,
      params: {
        limit: 250,
        _view: 'basic',
      },
    },
    {enabled: !!collection}
  );
  const features = useMemo(() => featuresData?.features || [], [featuresData]);

  const {
    data: reservationsData,
    isLoading: loadingReservations,
    error: reservationsError,
    refetch: refetchReservations,
  } = useGetReservationsByProduct(
    {
      collection_id: collection?.id,
      state: 'reserved',
    },
    {
      enabled: !!collection,
      refetchInterval: reservationPollIntervalMs,
    }
  );

  const onReserve = partial => {
    // do not clear things out if partial success
    if (!partial) {
      setSelectedProduct();
    }
    setReservationPollIntervalMs(SHORT_POLL_MS);
  };

  const onTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onSelectProduct = product => {
    setSelectedProduct(product);
  };

  const onZoomToFeature = () => {
    setZoomedFeature();
  };

  useEffect(() => {
    if (reservationsData?.reservations) {
      const selections = {};
      const productIds = Object.keys(reservationsData.reservations);
      productIds.forEach(productId => {
        selections[productId] = reservationsData.reservations[productId].map(
          r => r.aoi_ref
        );
      });

      setReservationsVisibleFeatures(selections);
    }
  }, [reservationsData?.reservations]);

  useEffect(() => {
    if (products && reservationsData?.reservations && features.length > 0) {
      const productIds = products?.map(p => p.id);
      const reservationRefs = {};
      productIds?.forEach(productId => {
        const refs =
          reservationsData?.reservations[productId]?.map(r =>
            getHashFromId(r.aoi_ref)
          ) ?? [];

        const toReserve = features.filter(
          f => !refs?.includes(getHashFromId(getFeatureReference(f)))
        );

        reservationRefs[productId] = toReserve;
      });

      setFeaturesToReserve(reservationRefs);
    }
  }, [selectedProduct, features, reservationsData, products]);

  const mapFeatures = useMemo(() => {
    if (activeTab === 'available') {
      return toReserveSelectedFeatureRefs;
    } else if (activeTab === 'reserved') {
      return Object.values(reservationsVisibleFeatures).flat();
    }
  }, [activeTab, reservationsVisibleFeatures, toReserveSelectedFeatureRefs]);

  if (!collection) {
    return false;
  }

  return (
    <DialogContent
      dividers
      sx={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <Stack
        direction="row"
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'static',
              padding: 2,
            }}
          >
            <Typography variant="h5" sx={{marginBottom: 1}}>
              Manage Quota
            </Typography>

            <Typography
              sx={{
                marginBottom: 2,
                color: 'text.secondary',
              }}
            >
              {`View estimates and reserve quota to use your products within your
              AOIs in the "Available" tab. View existing reservations in the
              "Reserved" tab.`}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1">Selected collection</Typography>
              <Chip
                label={
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="body2">
                      {`${collection.title} (${collection.feature_count})`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                      }}
                    >
                      <AreaIndicator squareMeters={collection.area} />
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          </Box>
          {productsError || reservationsError ? (
            <ErrorMessage
              statusCode={
                productsError?.response?.status ||
                reservationsError?.response?.status
              }
              onRetry={() => {
                refetchProducts();
                refetchReservations();
              }}
              message={`An error occurred while fetching your existing ${productsError ? 'products' : 'reservations'}.`}
            />
          ) : (
            <TabContext value={activeTab}>
              <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                <TabList onChange={onTabChange}>
                  <Tab label="Available" value="available" />
                  <Tab
                    label={`Reserved (${loadingReservations ? 'Loading...' : reservationsData?.productCount})`}
                    value="reserved"
                  />
                </TabList>
              </Box>
              <TabPanel
                value="available"
                sx={{
                  overflow: 'auto',
                  paddingTop: 2,
                  paddingBottom: 2,
                  paddingX: 2,
                  height: '100%',
                }}
              >
                <Stack sx={{gap: 2, height: '100%'}}>
                  <Grid container spacing={1}>
                    <Grid size={12}>
                      <ProductsDropDown
                        products={products}
                        value={selectedProduct}
                        onChange={onSelectProduct}
                        isLoading={loadingProducts}
                      />
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      flexGrow: 1,
                    }}
                  >
                    <AvailableTabContent
                      product={selectedProduct}
                      features={featuresToReserve[selectedProduct?.id] || []}
                      loading={loadingFeatures || loadingReservations}
                      selectedFeatureRefs={toReserveSelectedFeatureRefs}
                      setSelectedFeatureRefs={setToReserveSelectedFeatureRefs}
                      setZoomedFeature={setZoomedFeature}
                      onReserve={onReserve}
                      collection={collection}
                    />
                  </Box>
                </Stack>
              </TabPanel>
              <TabPanel
                value="reserved"
                sx={{
                  overflow: 'auto',
                  paddingTop: 2,
                  paddingBottom: 2,
                  paddingX: 2,
                  height: '100%',
                }}
              >
                <CompletedTabContent
                  products={products}
                  reservations={reservationsData?.reservations}
                  loadingReservations={loadingReservations}
                  productCount={reservationsData?.productCount}
                  features={features}
                  loadingFeatures={loadingFeatures}
                  selectedFeatureRefs={reservationsVisibleFeatures}
                  setSelectedFeatureRefs={setReservationsVisibleFeatures}
                  setZoomedFeature={setZoomedFeature}
                  collection={collection}
                />
              </TabPanel>
            </TabContext>
          )}
        </Box>
        <Box
          sx={{
            width: '50%',
            height: '100%',
          }}
        >
          <Map
            collection={collection}
            selectedFeatureRefs={mapFeatures}
            selectedStyle={getSelectedStyle(
              activeTab === 'available' ? pink : 'primary'
            )}
            baseStyle={getDefaultFeatureStyle(
              activeTab === 'available' ? pink : 'primary'
            )}
            zoomFeature={zoomedFeature}
            onZoomToFeature={onZoomToFeature}
            products={products}
            reservations={reservationsData?.list}
          />
        </Box>
      </Stack>
    </DialogContent>
  );
};

QuotaManagerContent.propTypes = {
  collection: object,
};

export default QuotaManagerContent;
